/* Scoped header geometry and mobile drawer actions.
   The existing theme remains responsible for menu open/close, back and focus. */
(() => {
  const DESKTOP_QUERY = "(min-width: 990px)";

  class VbmDrawerAction extends HTMLElement {
    connectedCallback() {
      if (this.desktopQuery) return;
      this.template = this.querySelector("template");
      if (!this.template) return;

      this.desktopQuery = window.matchMedia(DESKTOP_QUERY);
      this.onMediaChange = () => this.update();
      this.desktopQuery.addEventListener("change", this.onMediaChange);
      this.update();
    }

    update() {
      if (this.desktopQuery.matches) {
        this.link?.remove();
        this.link = null;
        this.removeAttribute("data-active");
        return;
      }

      if (!this.link) {
        this.append(this.template.content.cloneNode(true));
        this.link = this.querySelector("[data-vbm-measurement-link]");
      }
      this.toggleAttribute("data-active", Boolean(this.link));
    }

    disconnectedCallback() {
      this.desktopQuery?.removeEventListener("change", this.onMediaChange);
      this.desktopQuery = null;
      this.link?.remove();
      this.link = null;
      this.removeAttribute("data-active");
    }
  }

  class VbmHeaderController extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;
      this.wrapper = this.closest(".vbm-header");
      this.header = this.wrapper?.querySelector("header.header");
      this.section = this.closest(".section-header");
      if (!this.wrapper || !this.header || !this.section) return;

      this.drawer = this.wrapper.querySelector("header-drawer");
      this.abortController = new AbortController();
      const { signal } = this.abortController;
      this.scheduleUpdate = () => {
        if (this.frame) return;
        this.frame = window.requestAnimationFrame(() => {
          this.frame = null;
          this.updateGeometry();
        });
      };

      this.desktopQuery = window.matchMedia(DESKTOP_QUERY);
      this.onBreakpointChange = () => {
        // The native focus trap stores its first/last elements. Close an open
        // drawer when its mobile-only action is added or removed by resizing.
        const details = this.drawer?.querySelector("details");
        const summary = details?.querySelector("summary");
        if (
          details?.open &&
          summary &&
          typeof this.drawer.closeMenuDrawer === "function"
        ) {
          this.drawer.closeMenuDrawer(new Event("resize"), summary);
          summary.setAttribute("aria-expanded", "false");
        }
        this.scheduleUpdate();
      };
      this.desktopQuery.addEventListener("change", this.onBreakpointChange);

      this.resizeObserver = new ResizeObserver(this.scheduleUpdate);
      this.resizeObserver.observe(this.header);
      this.resizeObserver.observe(this.wrapper);
      window.addEventListener("resize", this.scheduleUpdate, {
        passive: true,
        signal,
      });
      window.addEventListener("scroll", this.scheduleUpdate, {
        passive: true,
        signal,
      });
      window.visualViewport?.addEventListener("resize", this.scheduleUpdate, {
        passive: true,
        signal,
      });
      window.visualViewport?.addEventListener("scroll", this.scheduleUpdate, {
        passive: true,
        signal,
      });
      this.wrapper.addEventListener("toggle", this.scheduleUpdate, {
        capture: true,
        signal,
      });
      this.updateGeometry();
    }

    updateGeometry() {
      if (!this.isConnected) return;
      const row = this.header.getBoundingClientRect();
      const wrapper = this.wrapper.getBoundingClientRect();
      const viewport = window.visualViewport;
      const viewportTop = viewport?.offsetTop || 0;
      const viewportBottom =
        viewportTop + (viewport?.height || window.innerHeight);
      const menuOffset = Math.max(0, row.bottom - wrapper.top);
      const menuHeight = Math.max(
        0,
        viewportBottom - Math.max(viewportTop, row.bottom),
      );

      this.wrapper.style.setProperty("--vbm-menu-offset", `${menuOffset}px`);
      this.wrapper.style.setProperty("--vbm-menu-height", `${menuHeight}px`);
      document.documentElement.style.setProperty(
        "--header-height",
        `${this.section.offsetHeight}px`,
      );
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.abortController = null;
      this.resizeObserver?.disconnect();
      this.desktopQuery?.removeEventListener("change", this.onBreakpointChange);
      window.cancelAnimationFrame(this.frame);
      this.frame = null;
    }
  }

  if (!customElements.get("vbm-drawer-action")) {
    customElements.define("vbm-drawer-action", VbmDrawerAction);
  }
  if (!customElements.get("vbm-header-controller")) {
    customElements.define("vbm-header-controller", VbmHeaderController);
  }
})();
